// sensei-app/client/ui/StudentAssignmentView.js
import { ComponentBase } from "../../core/ComponentBase.js";
import { marked } from "https://cdn.jsdelivr.net/npm/marked/lib/marked.esm.js";

export class StudentAssignmentView extends ComponentBase {
    static styles = `
        .assignment-view-container { display: flex; height: 100vh; width: 100vw; background-color: var(--bs-tertiary-bg); }
        .assignment-sidebar { width: 40%; max-width: 600px; background-color: var(--bs-body-bg); padding: 2rem; overflow-y: auto; border-right: 1px solid var(--bs-border-color); }
        .assignment-main-content { flex-grow: 1; padding: 2rem; display: flex; flex-direction: column; justify-content: center; align-items: center; }
        .submission-box { width: 100%; max-width: 700px; background-color: var(--bs-body-bg); padding: 2rem; border-radius: var(--bs-border-radius-lg); box-shadow: var(--bs-box-shadow); }
        .submission-box h3 { margin-top: 0; }
        .plagiarism-check { margin-top: 1.5rem; }
    `;

    constructor(config) {
        super(config);
        this.scheduleId = null;
        this.assignmentDetails = null;
        this.fileUploadComponent = null;
        this.isSubmitting = false;
        this.isLoading = true;
        this.applyStyles();
    }

    async initialize() {
        // --- THIS IS THE CRITICAL FIX ---
        // 1. Create the main container synchronously. It will exist before render() is ever called.
        this.container = document.createElement('div');
        this.container.className = 'assignment-view-container';

        // 3. Create the managed component after super() so appClient is available.
        this.fileUploadComponent = await this.createManagedComponent('FileUpload', null, {
            instanceId: `${this.config.instanceId}-assignment-upload`,
            getUploadKey: async () => {
                return await this.appClient.rpcClient.call('StudentAssignmentRpc.getSubmissionUploadKey', {
                    scheduleId: this.scheduleId
                });
            },
            label: 'Upload your files (PDF or TXT)',
            showLabel: true
        });

        await super.initialize();
    }

    async initAfterLogin() {
        const queryParams = this.appClient.layoutEngine.getQueryParams();
        const scheduleIdFromRoute = queryParams.scheduleId;

        if (this.scheduleId !== scheduleIdFromRoute) {
            this.scheduleId = scheduleIdFromRoute;
            if (this.scheduleId) {
                await this.loadDetails();
            } else {
                this.showError("No assignment schedule specified.");
            }
        }
    }

    async onShow() {
        this.isSubmitting = false;
        this.assignmentDetails = null;
        this.isLoading = true;
        this.fileUploadComponent.clear();

        if (this.isAuthInitialized) {
            await this.initAfterLogin();
        }
    }

    async loadDetails() {
        this.isLoading = true;
        this.render();
        try {
            this.assignmentDetails = await this.appClient.rpcClient.call('StudentAssignmentRpc.getAssignmentDetails', { scheduleId: this.scheduleId });
        } catch (error) {
            this.showError(`Could not load assignment details: ${error.message}`);
        } finally {
            this.isLoading = false;
            this.render();
        }
    }

    render() {
        // Now, this.container is guaranteed to exist.
        this.container.innerHTML = '';

        if (this.isLoading) {
            this.container.innerHTML = `
                <div class="d-flex justify-content-center align-items-center h-100 w-100">
                    <div class="text-center">
                        <div class="spinner-border text-primary" role="status"><span class="visually-hidden">Loading...</span></div>
                        <p class="mt-2 text-muted">${this.t('Loading Assignment...')}</p>
                    </div>
                </div>`;
            return this.container;
        }

        if (!this.assignmentDetails) {
            this.showError("Assignment details could not be loaded.");
            return this.container;
        }

        const sidebar = document.createElement('div');
        sidebar.className = 'assignment-sidebar';
        sidebar.innerHTML = `
            <h2 class="mb-3">${this.assignmentDetails.topic.title}</h2>
            <p class="text-muted">Course: ${this.assignmentDetails.topic.title}</p>
            <hr class="my-4">
            <div class="assignment-prompt"></div>
        `;
        sidebar.querySelector('.assignment-prompt').innerHTML = marked.parse(this.assignmentDetails.topicContent);

        const mainContent = document.createElement('div');
        mainContent.className = 'assignment-main-content';
        mainContent.innerHTML = `
            <div class="submission-box">
                <h3>Submit Your Work</h3>
                <p class="text-muted">Upload your completed assignment files. Accepted formats: PDF, TXT.</p>
                <div id="file-upload-container" class="my-4"></div>
                <div class="form-check plagiarism-check">
                    <input class="form-check-input" type="checkbox" value="" id="plagiarism-ack">
                    <label class="form-check-label" for="plagiarism-ack">
                        I acknowledge that this submission is my own original work and adheres to the academic integrity policy.
                    </label>
                </div>
                <div class="d-flex justify-content-between mt-4">
                    <button class="btn btn-secondary" id="back-btn">Back to Schedule</button>
                    <button class="btn btn-primary" id="submit-btn" disabled>Submit Assignment</button>
                </div>
            </div>
        `;

        mainContent.querySelector('#file-upload-container').appendChild(this.fileUploadComponent.container);
        mainContent.querySelector('#back-btn').addEventListener('click', () => this.appClient.layoutEngine.navigate('/student/schedule'));
        mainContent.querySelector('#submit-btn').addEventListener('click', () => this.handleSubmit());
        mainContent.querySelector('#plagiarism-ack').addEventListener('change', (e) => {
            mainContent.querySelector('#submit-btn').disabled = !e.target.checked;
        });

        this.container.append(sidebar, mainContent);
        return this.container;
    }

    async handleSubmit() {
        if (this.isSubmitting) return;

        const uploadKey = this.fileUploadComponent.getValue();
        if (!uploadKey) {
            this.appClient.notifier.warn("Please upload at least one file for your submission.");
            return;
        }

        this.isSubmitting = true;
        const submitBtn = this.container.querySelector('#submit-btn');
        submitBtn.disabled = true;
        submitBtn.innerHTML = `<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Submitting...`;

        try {
            await this.appClient.rpcClient.call('StudentAssignmentRpc.submitAssignment', {
                scheduleId: this.scheduleId,
                uploadKey: uploadKey
            });
            await this.appClient.notifier.alert("Your assignment has been submitted successfully!", { type: 'success' });
            this.appClient.layoutEngine.navigate('/student/schedule');
        } catch (error) {
            this.appClient.notifier.error(`Submission failed: ${error.message}`);
        } finally {
            this.isSubmitting = false;
        }
    }

    showError(message) {
        if (this.container) {
            this.container.innerHTML = `<div class="alert alert-danger m-3">${message}</div>`;
        }
    }
    destroy() { super.destroy(); }
}